// Content Adapter - Bridges Contentful CMS with static fallback data
// This adapter allows the app to work with Contentful when content is available,
// and falls back to static data when Contentful content doesn't exist yet.

import {
  fetchEntries,
  fetchEntryBySlug,
  fetchEntriesByField,
  getImageUrl,
  CONTENT_TYPES,
} from './contentful';

import {
  videosData,
  blogPosts,
  booksData,
  marketplaceData,
  topicsData,
  awardsData,
  aboutData,
  siteConfig,
  servicesData,
} from '../data/siteData';

// Check if Contentful is configured
const isContentfulConfigured = () => {
  return !!(
    process.env.REACT_APP_CONTENTFUL_SPACE_ID &&
    process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN
  );
};

/**
 * Transform Contentful video entry to match static data format
 */
const transformContentfulVideo = (entry) => {
  const fields = entry.fields;
  return {
    id: entry.sys.id,
    slug: fields.slug,
    title: fields.title,
    description: fields.description,
    fullDescription: fields.fullDescription || fields.description,
    category: fields.category?.toLowerCase() || 'podcast',
    thumbnail: fields.thumbnail ? getImageUrl(fields.thumbnail, { width: 600 }) : null,
    duration: fields.duration,
    date: fields.publishDate || entry.sys.createdAt,
    embedUrl: fields.videoUrl || fields.embedUrl,
    guestName: fields.guestName,
    guestTitle: fields.guestTitle,
    guestBio: fields.guestBio,
    guestImage: fields.guestImage ? getImageUrl(fields.guestImage, { width: 400 }) : null,
    guestResources: fields.guestResources || [], // Array of { title, url }
    transcript: fields.transcript,
    platforms: fields.platforms || [],
    clips: (fields.clips || []).map(clip => ({
      title: clip.title || clip.fields?.title,
      description: clip.description || clip.fields?.description,
      timestamp: clip.timestamp || clip.fields?.timestamp,
      duration: clip.duration || clip.fields?.duration,
      url: clip.url || clip.fields?.url,
      thumbnail: clip.thumbnail ? getImageUrl(clip.thumbnail, { width: 300 }) : 
                 clip.fields?.thumbnail ? getImageUrl(clip.fields.thumbnail, { width: 300 }) : null,
    })),
  };
};

/**
 * Transform Contentful blog entry to match static data format
 */
const transformContentfulBlog = (entry) => {
  const fields = entry.fields;
  return {
    id: entry.sys.id,
    slug: fields.slug,
    title: fields.title,
    excerpt: fields.excerpt,
    content: fields.content, // Rich text - needs RichTextRenderer
    contentRaw: fields.content, // Keep raw for rich text rendering
    author: fields.author || 'Gaurav',
    date: fields.publishDate || entry.sys.createdAt,
    readTime: fields.readTime || '5 min read',
    image: fields.featuredImage ? getImageUrl(fields.featuredImage, { width: 800 }) : null,
    tags: fields.tags || [],
    topic: fields.topic || 'leadership',
  };
};

/**
 * Transform Contentful book entry to match static data format
 */
const transformContentfulBook = (entry) => {
  const fields = entry.fields;
  return {
    id: entry.sys.id,
    slug: fields.slug,
    title: fields.title,
    subtitle: fields.subtitle,
    description: fields.description,
    fullDescription: fields.fullDescription, // Rich text
    fullDescriptionRaw: fields.fullDescription,
    coverImage: fields.coverImage ? getImageUrl(fields.coverImage, { width: 400 }) : null,
    publishDate: fields.publishDate,
    publisher: fields.publisher,
    pages: fields.pages,
    isbn: fields.isbn,
    amazonUrl: fields.amazonUrl,
    barnesNobleUrl: fields.barnesNobleUrl,
    audibleUrl: fields.audibleUrl,
    kindleUrl: fields.kindleUrl,
    featured: fields.featured || false,
    testimonials: fields.testimonials || [],
    chapters: fields.chapters || [],
    keyTakeaways: fields.keyTakeaways || [],
  };
};

/**
 * Transform Contentful marketplace entry to match static data format
 */
const transformContentfulMarketplace = (entry) => {
  const fields = entry.fields;
  return {
    id: entry.sys.id,
    name: fields.name || fields.productName,
    description: fields.description,
    price: fields.price,
    originalPrice: fields.originalPrice,
    image: fields.image ? getImageUrl(fields.image, { width: 400 }) : null,
    images: fields.images?.map(img => getImageUrl(img, { width: 400 })) || [],
    category: fields.category,
    featured: fields.featured || false,
    features: fields.features || [],
  };
};

/**
 * Fetch all videos - tries Contentful first, falls back to static data
 */
export const getVideos = async () => {
  if (!isContentfulConfigured()) {
    return videosData;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.VIDEO, {
      order: '-fields.publishDate',
    });
    
    if (entries && entries.length > 0) {
      return entries.map(transformContentfulVideo);
    }
    
    // Fallback to static data if no Contentful content
    return videosData;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return videosData;
  }
};

/**
 * Fetch videos by category
 */
export const getVideosByCategory = async (category) => {
  if (!isContentfulConfigured()) {
    return videosData.filter(v => v.category === category);
  }

  try {
    const entries = await fetchEntriesByField(CONTENT_TYPES.VIDEO, 'category', category, {
      order: '-fields.publishDate',
    });
    
    if (entries && entries.length > 0) {
      return entries.map(transformContentfulVideo);
    }
    
    return videosData.filter(v => v.category === category);
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return videosData.filter(v => v.category === category);
  }
};

/**
 * Fetch a single video by slug
 */
export const getVideoBySlug = async (slug) => {
  if (!isContentfulConfigured()) {
    return videosData.find(v => v.slug === slug) || null;
  }

  try {
    const entry = await fetchEntryBySlug(CONTENT_TYPES.VIDEO, slug);
    
    if (entry) {
      return transformContentfulVideo(entry);
    }
    
    return videosData.find(v => v.slug === slug) || null;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return videosData.find(v => v.slug === slug) || null;
  }
};

/**
 * Fetch all blog posts
 */
export const getBlogPosts = async () => {
  if (!isContentfulConfigured()) {
    return blogPosts;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.BLOG_POST, {
      order: '-fields.publishDate',
    });
    
    if (entries && entries.length > 0) {
      return entries.map(transformContentfulBlog);
    }
    
    return blogPosts;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return blogPosts;
  }
};

/**
 * Fetch a single blog post by slug
 */
export const getBlogBySlug = async (slug) => {
  if (!isContentfulConfigured()) {
    return blogPosts.find(b => b.slug === slug) || null;
  }

  try {
    const entry = await fetchEntryBySlug(CONTENT_TYPES.BLOG_POST, slug);
    
    if (entry) {
      return transformContentfulBlog(entry);
    }
    
    return blogPosts.find(b => b.slug === slug) || null;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return blogPosts.find(b => b.slug === slug) || null;
  }
};

/**
 * Fetch all books
 */
export const getBooks = async () => {
  if (!isContentfulConfigured()) {
    return booksData;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.BOOK, {
      order: '-fields.publishDate',
    });
    
    if (entries && entries.length > 0) {
      return entries.map(transformContentfulBook);
    }
    
    return booksData;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return booksData;
  }
};

/**
 * Fetch a single book by slug
 */
export const getBookBySlug = async (slug) => {
  if (!isContentfulConfigured()) {
    return booksData.find(b => b.slug === slug) || null;
  }

  try {
    const entry = await fetchEntryBySlug(CONTENT_TYPES.BOOK, slug);
    
    if (entry) {
      return transformContentfulBook(entry);
    }
    
    return booksData.find(b => b.slug === slug) || null;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return booksData.find(b => b.slug === slug) || null;
  }
};

/**
 * Fetch all marketplace items
 */
export const getMarketplaceItems = async () => {
  if (!isContentfulConfigured()) {
    return marketplaceData;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.MARKETPLACE_ITEM);
    
    if (entries && entries.length > 0) {
      return entries.map(transformContentfulMarketplace);
    }
    
    return marketplaceData;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return marketplaceData;
  }
};

/**
 * Fetch topics - usually static but can be managed in Contentful
 */
export const getTopics = async () => {
  if (!isContentfulConfigured()) {
    return topicsData;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.TOPIC);
    
    if (entries && entries.length > 0) {
      return entries.map(entry => ({
        id: entry.fields.id || entry.sys.id,
        name: entry.fields.name,
        description: entry.fields.description,
        color: entry.fields.color || '#F97316',
        icon: entry.fields.icon,
      }));
    }
    
    return topicsData;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return topicsData;
  }
};

/**
 * Fetch awards
 */
export const getAwards = async () => {
  if (!isContentfulConfigured()) {
    return awardsData;
  }

  try {
    const entries = await fetchEntries(CONTENT_TYPES.AWARD, {
      order: '-fields.year',
    });
    
    if (entries && entries.length > 0) {
      return entries.map(entry => ({
        id: entry.sys.id,
        title: entry.fields.title,
        organization: entry.fields.organization,
        year: entry.fields.year,
        description: entry.fields.description,
      }));
    }
    
    return awardsData;
  } catch (error) {
    console.warn('Failed to fetch from Contentful, using static data:', error);
    return awardsData;
  }
};

/**
 * Fetch about data
 */
export const getAboutData = async () => {
  // About data is typically static, but can be managed in Contentful
  return aboutData;
};

/**
 * Fetch site configuration
 */
export const getSiteConfig = async () => {
  // Site config is typically static
  return siteConfig;
};

/**
 * Fetch services data
 */
export const getServicesData = async () => {
  return servicesData;
};

/**
 * Export helper to check Contentful status
 */
export const getContentSource = () => {
  return isContentfulConfigured() ? 'contentful' : 'static';
};
